<template>
  <div class="question" role="group" :aria-label="question.question">
    <div class="question__head">
      <span class="question__header">{{ question.header }}</span>
      <span class="question__text">{{ question.question }}</span>
      <span v-if="!locked" class="question__hint">{{
        multi ? "Pick any" : "Pick one"
      }}</span>
    </div>

    <OptionRow
      v-for="(row, i) in rows"
      :key="i"
      :selected="answer.picked.includes(i)"
      :multi="multi"
      :locked="locked"
      :marked="marked"
      @pick="pick(i)"
    >
      <slot name="row" :row="row" :selected="answer.picked.includes(i)"></slot>
    </OptionRow>
    <OptionRow
      :selected="answer.otherOpen"
      :multi="multi"
      :locked="locked"
      @pick="toggleOther"
    >
      <span class="question__other">
        <span class="question__other-label">Something else…</span>
        <span class="question__other-hint">{{ otherHint }}</span>
      </span>
    </OptionRow>

    <SomethingElse
      v-if="answer.otherOpen && !locked"
      :model-value="answer.other"
      :label="`${question.header}: something else`"
      :sendable="sendable"
      @update:model-value="update({ other: $event })"
      @send="$emit('send')"
    />
  </div>
</template>

<script>
import OptionRow from "./OptionRow.vue";
import SomethingElse from "./SomethingElse.vue";

export default {
  name: "Question",
  components: { OptionRow, SomethingElse },
  props: {
    question: { type: Object, required: true },
    rows: { type: Array, required: true },
    /** `{picked: [row index], otherOpen, other}` */
    answer: { type: Object, required: true },
    locked: { type: Boolean, default: false },
    /** Rows lead with a radio or a check box, unless they show their own mark. */
    marked: { type: Boolean, default: true },
    /** This question alone answers the card, so its typed words send themselves. */
    sendable: { type: Boolean, default: false },
  },
  emits: ["update", "picked", "send"],
  computed: {
    multi() {
      return Boolean(this.question.multi_select);
    },
    /** On an answered card, the words typed; until then, what the row is for. */
    otherHint() {
      return this.locked && this.answer.other
        ? this.answer.other
        : "Type it in your own words";
    },
  },
  methods: {
    update(change) {
      this.$emit("update", { ...this.answer, ...change });
    },
    pick(i) {
      if (!this.multi) {
        this.update({ picked: [i], otherOpen: false });
        this.$emit("picked");
        return;
      }
      const picked = this.answer.picked.includes(i)
        ? this.answer.picked.filter((p) => p !== i)
        : [...this.answer.picked, i];
      this.update({ picked });
    },
    toggleOther() {
      if (!this.multi) {
        this.update({ otherOpen: true, picked: [] });
        return;
      }
      this.update({ otherOpen: !this.answer.otherOpen });
    },
  },
};
</script>

<style lang="scss" scoped>
.question {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.question__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
}

.question__header {
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--oliver-accent-light);
  color: var(--oliver-accent);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.question__text {
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.question__hint {
  font-size: 11px;
  color: var(--oliver-text-muted);
}

.question__other {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.question__other-label {
  font-size: 13px;
  font-weight: 600;
}

.question__other-hint {
  overflow-wrap: anywhere;
  white-space: pre-line;
  font-size: 11px;
  color: var(--oliver-text-muted);
}
</style>
